import {
  Directive, Input, TemplateRef, ViewContainerRef, Output, EventEmitter,
  Renderer, ElementRef, OnInit, OnDestroy
} from '@angular/core';
import { TooltipContainerComponent } from './tooltip-container.component';
import { TooltipConfig } from './tooltip.config';
import { ComponentLoaderFactory, ComponentLoader } from '../component-loader';
import { OnChange } from '../utils/decorators';
import { warnOnce } from '../utils/warn-once';


export class TooltipDirective implements OnInit, OnDestroy {
  /**
   * Content to be displayed as tooltip.
   */
  @OnChange()
   public tooltip: string | TemplateRef<any>;
  /** Fired when tooltip content changes */
   public tooltipChange: EventEmitter<string | TemplateRef<any>> = new EventEmitter();

  /**
   * Placement of a tooltip. Accepts: "top", "bottom", "left", "right"
   */
   public placement: string;
  /**
   * Specifies events that should trigger. Supports a space separated list of
   * event names.
   */
   public triggers: string;
  /**
   * A selector specifying the element the tooltip should be appended to.
   * Currently only supports "body".
   */
   public container: string;

  /**
   * Returns whether or not the tooltip is currently being shown
   */
  
  public get isOpen(): boolean { return this._tooltip.isShown; }

  public set isOpen(value: boolean) {
    if (value) {this.show();} else {this.hide();}
  }

  /**
   * Allows to disable tooltip
   */
   public isDisabled: boolean;

  /**
   * Css class for tooltip container
   */
   public containerClass: string = '';

  /**
   * Emits an event when the tooltip is shown
   */
   public onShown: EventEmitter<any>;
  /**
   * Emits an event when the tooltip is hidden
   */
   public onHidden: EventEmitter<any>;

  /* tslint:disable */
  /** @deprecated - please use `tooltip` instead */
  
  public set htmlContent(value: string | TemplateRef<any>) {
    warnOnce('tooltipHtml was deprecated, please use `tooltip` instead');
    this.tooltip = value;
  }

  /** @deprecated - please use `placement` instead */
  
  public set _placement(value: string) {
    warnOnce('tooltipPlacement was deprecated, please use `placement` instead');
    this.placement = value;
  }

  /** @deprecated - please use `isOpen` instead*/
  
  public set _isOpen(value: boolean) {
    warnOnce('tooltipIsOpen was deprecated, please use `isOpen` instead');
    this.isOpen = value;
  }

  public get _isOpen(): boolean {
    warnOnce('tooltipIsOpen was deprecated, please use `isOpen` instead');
    return this.isOpen;
  }

  /** @deprecated - please use `isDisabled` instead */
  
  public set _enable(value: boolean) {
    warnOnce('tooltipEnable was deprecated, please use `isDisabled` instead');
    this.isDisabled = value === true;
  }

  public get _enable(): boolean {
    warnOnce('tooltipEnable was deprecated, please use `isDisabled` instead');
    return this.isDisabled === true;
  }

  /** @deprecated - please use `container="body"` instead */
  
  public set _appendToBody(value: boolean) {
    warnOnce('tooltipAppendToBody was deprecated, please use `container="body"` instead');
    this.container = value ? 'body' : this.container;
  }

  public get _appendToBody(): boolean {
    warnOnce('tooltipAppendToBody was deprecated, please use `container="body"` instead');
    return this.container === 'body';
  }

  /** @deprecated - removed, will be added to configuration */
   public _animation: boolean = true;

  /** @deprecated - will replaced with customClass */
  
  public set _popupClass(value: string) {
    warnOnce('tooltipClass deprecated');
  }

  /** @deprecated - removed */
  
  public set _tooltipContext(value: any) {
    warnOnce('tooltipContext deprecated');
  }

  /** @deprecated */
   public _delay: number = 0;

  /** @deprecated */
   public _fadeDuration: number = 150;

  /** @deprecated -  please use `triggers` instead */
  
  public get _tooltipTrigger(): string|Array<string> {
    warnOnce('tooltipTrigger was deprecated, please use `triggers` instead');
    return this.triggers;
  };

  public set _tooltipTrigger(value: string|Array<string>) {
    warnOnce('tooltipTrigger was deprecated, please use `triggers` instead');
    this.triggers = (value || '').toString();
  };

  /** @deprecated */
   public tooltipStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  /* tslint:enable */
  protected _delayTimeoutId: number | any;

  private _tooltip: ComponentLoader<TooltipContainerComponent>;

  // tslint:disable-next-line
  public constructor(_viewContainerRef: ViewContainerRef,
                     _renderer: Renderer,
                     _elementRef: ElementRef,
                     cis: ComponentLoaderFactory,
                     config: TooltipConfig) {
    this._tooltip = cis
      .createLoader<TooltipContainerComponent>(_elementRef, _viewContainerRef, _renderer)
      .provide({provide: TooltipConfig, useValue: config});

    Object.assign(this, config);
    this.onShown = this._tooltip.onShown;
    this.onHidden = this._tooltip.onHidden;
  }

  public ngOnInit(): void {
    this._tooltip.listen({
      triggers: this.triggers,
      show: () => this.show()
    });
    this.tooltipChange.subscribe((value: any) => {
      if (!value) {
        this._tooltip.hide();
      }
    });
  }

  /**
   * Toggles an element’s tooltip. This is considered a “manual” triggering of
   * the tooltip.
   */
  public toggle(): void {
    if (this.isOpen) {
      return this.hide();
    }

    this.show();
  }

  /**
   * Opens an element’s tooltip. This is considered a “manual” triggering of
   * the tooltip.
   */
  public show(): void {
    if (this.isOpen || this.isDisabled || this._delayTimeoutId || !this.tooltip) {
      return;
    }

    const showTooltip = () => this._tooltip
      .attach(TooltipContainerComponent)
      .to(this.container)
      .position({attachment: this.placement})
      .show({
        content: this.tooltip,
        placement: this.placement,
        containerClass: this.containerClass
      });

    if (this._delay) {
      this._delayTimeoutId = setTimeout(() => { showTooltip(); }, this._delay);
    } else {
      showTooltip();
    }
  }

  /**
   * Closes an element’s tooltip. This is considered a “manual” triggering of
   * the tooltip.
   */
  public hide(): void {
    if (this._delayTimeoutId) {
      clearTimeout(this._delayTimeoutId);
      this._delayTimeoutId = undefined;
    }

    if (!this._tooltip.isShown) {
      return;
    }

    this._tooltip.instance.classMap.in = false;
    setTimeout(() => {
      this._tooltip.hide();
    }, this._fadeDuration);
  }

  public ngOnDestroy(): void {
    this._tooltip.dispose();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[tooltip], [tooltipHtml]',
  exportAs: 'bs-tooltip'
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ViewContainerRef, },
{type: Renderer, },
{type: ElementRef, },
{type: ComponentLoaderFactory, },
{type: TooltipConfig, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'tooltip': [{ type: Input },],
'tooltipChange': [{ type: Output },],
'placement': [{ type: Input },],
'triggers': [{ type: Input },],
'container': [{ type: Input },],
'isOpen': [{ type: Input },],
'isDisabled': [{ type: Input },],
'containerClass': [{ type: Input },],
'onShown': [{ type: Output },],
'onHidden': [{ type: Output },],
'htmlContent': [{ type: Input, args: ['tooltipHtml', ] },],
'_placement': [{ type: Input, args: ['tooltipPlacement', ] },],
'_isOpen': [{ type: Input, args: ['tooltipIsOpen', ] },],
'_enable': [{ type: Input, args: ['tooltipEnable', ] },],
'_appendToBody': [{ type: Input, args: ['tooltipAppendToBody', ] },],
'_animation': [{ type: Input, args: ['tooltipAnimation', ] },],
'_popupClass': [{ type: Input, args: ['tooltipClass', ] },],
'_tooltipContext': [{ type: Input, args: ['tooltipContext', ] },],
'_delay': [{ type: Input, args: ['tooltipPopupDelay', ] },],
'_fadeDuration': [{ type: Input, args: ['tooltipFadeDuration', ] },],
'_tooltipTrigger': [{ type: Input, args: ['tooltipTrigger', ] },],
'tooltipStateChanged': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
